import React, { useState, useEffect } from 'react';
import { 
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  Area, AreaChart
} from 'recharts';
import { 
  Clock, TrendingUp, TrendingDown, Activity, 
  Server, AlertTriangle, Filter, Download, 
  RefreshCw, Calendar, Database, Zap, LayoutDashboard
} from 'lucide-react';
import { useAuth } from '../../../../contexts/AuthContext';

const PerformanceMonitoring = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [endpoints, setEndpoints] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [slowRequests, setSlowRequests] = useState([]);
  const [error, setError] = useState('');
  const { user } = useAuth();

  // Filters
  const [filters, setFilters] = useState({
    startDate: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString().split('T')[0], // Last 24 hours
    endDate: new Date().toISOString().split('T')[0],
    type: 'request',
    path: '',
    method: '',
    tenantCode: '',
    minDuration: '',
    interval: '1h'
  });

  const [activeView, setActiveView] = useState('overview'); // 'overview', 'endpoints', 'timeline', 'tenants', 'slow'

  useEffect(() => {
    loadData();
  }, [filters.startDate, filters.endDate, filters.type, filters.tenantCode, filters.interval]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');

      const token = localStorage.getItem('token');
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      // Build query params
      const queryParams = new URLSearchParams();
      if (filters.startDate) queryParams.append('startDate', filters.startDate);
      if (filters.endDate) queryParams.append('endDate', filters.endDate);
      if (filters.type) queryParams.append('type', filters.type);
      if (filters.tenantCode) queryParams.append('tenantCode', filters.tenantCode);
      if (filters.minDuration) queryParams.append('minDuration', filters.minDuration);

      // Load all data in parallel
      const [statsRes, endpointsRes, timelineRes, tenantsRes, slowRes] = await Promise.all([
        fetch(`/api/admin/performance/stats?${queryParams.toString()}&limit=500`, { headers }),
        fetch(`/api/admin/performance/endpoints?${queryParams.toString()}&limit=20`, { headers }),
        fetch(`/api/admin/performance/timeline?${queryParams.toString()}&interval=${filters.interval}`, { headers }),
        fetch(`/api/admin/performance/tenants?${queryParams.toString()}`, { headers }),
        fetch(`/api/admin/performance/slow-requests?${queryParams.toString()}&limit=50`, { headers })
      ]);

      const statsData = await statsRes.json();
      const endpointsData = await endpointsRes.json();
      const timelineData = await timelineRes.json();
      const tenantsData = await tenantsRes.json();
      const slowData = await slowRes.json();

      if (statsData.success) setStats(statsData.stats);
      if (endpointsData.success) setEndpoints(endpointsData.endpoints);
      if (timelineData.success) setTimeline(timelineData.timeline);
      if (tenantsData.success) setTenants(tenantsData.tenants);
      if (slowData.success) setSlowRequests(slowData.slowRequests);

    } catch (err) {
      console.error('Error loading performance data:', err);
      setError('Failed to load performance data: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const clearLogs = async () => {
    if (!window.confirm('Are you sure you want to clear performance logs? This will archive current logs.')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/admin/performance/clear-logs', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ archive: true })
      });

      const data = await response.json();
      if (data.success) {
        alert('Logs cleared and archived successfully');
        loadData();
      } else {
        alert('Failed to clear logs: ' + data.message);
      }
    } catch (err) {
      alert('Error clearing logs: ' + err.message);
    }
  };

  const exportData = () => {
    const dataToExport = {
      stats,
      endpoints,
      timeline,
      tenants,
      slowRequests,
      exportedAt: new Date().toISOString(),
      filters
    };

    const blob = new Blob([JSON.stringify(dataToExport, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `performance-report-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
  };

  const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

  if (loading && !stats) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading performance data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-red-600 dark:text-red-400">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Activity className="w-8 h-8 text-blue-500" />
            Performance Monitoring
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            System performance analytics and monitoring
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={loadData}
            disabled={loading}
            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 flex items-center gap-2 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            onClick={exportData}
            className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 flex items-center gap-2"
          >
            <Download className="w-4 h-4" />
            Export
          </button>
          <button
            onClick={clearLogs}
            className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 flex items-center gap-2"
          >
            <AlertTriangle className="w-4 h-4" />
            Clear Logs
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-2 mb-3">
          <Filter className="w-5 h-5 text-gray-600 dark:text-gray-400" />
          <h3 className="font-semibold text-gray-900 dark:text-white">Filters</h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Start Date
            </label>
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              End Date
            </label>
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Type
            </label>
            <select
              value={filters.type}
              onChange={(e) => setFilters({ ...filters, type: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="">All</option>
              <option value="request">Requests</option>
              <option value="operation">Operations</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Tenant
            </label>
            <input
              type="text"
              value={filters.tenantCode}
              onChange={(e) => setFilters({ ...filters, tenantCode: e.target.value })}
              placeholder="All tenants"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Min Duration (ms)
            </label>
            <input
              type="number"
              value={filters.minDuration}
              onChange={(e) => setFilters({ ...filters, minDuration: e.target.value })}
              placeholder="e.g., 100"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Interval
            </label>
            <select
              value={filters.interval}
              onChange={(e) => setFilters({ ...filters, interval: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="1m">1 Minute</option>
              <option value="5m">5 Minutes</option>
              <option value="15m">15 Minutes</option>
              <option value="1h">1 Hour</option>
              <option value="6h">6 Hours</option>
              <option value="1d">1 Day</option>
            </select>
          </div>
        </div>
      </div>

      {/* View Tabs */}
      <div className="flex gap-2 border-b border-gray-200 dark:border-gray-700">
        {[
          { id: 'overview', label: 'Overview', icon: LayoutDashboard },
          { id: 'timeline', label: 'Timeline', icon: TrendingUp },
          { id: 'endpoints', label: 'Endpoints', icon: Server },
          { id: 'tenants', label: 'Tenants', icon: Database },
          { id: 'slow', label: 'Slow Requests', icon: AlertTriangle }
        ].map(tab => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveView(tab.id)}
              className={`px-4 py-2 flex items-center gap-2 border-b-2 transition-colors ${
                activeView === tab.id
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
            >
              <Icon className="w-4 h-4" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Overview View */}
      {activeView === 'overview' && stats && (
        <div className="space-y-6">
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
              title="Total Requests"
              value={stats.requests?.toLocaleString() || '0'}
              icon={<Activity className="w-6 h-6 text-blue-500" />}
              color="blue"
            />
            <StatCard
              title="Avg Response Time"
              value={`${stats.avgRequestDuration || 0}ms`}
              icon={<Clock className="w-6 h-6 text-green-500" />}
              color="green"
              subtitle={`P95: ${stats.p95}ms`}
            />
            <StatCard
              title="Slow Requests"
              value={stats.slowRequests?.toLocaleString() || '0'}
              icon={<AlertTriangle className="w-6 h-6 text-orange-500" />}
              color="orange"
              subtitle={`${((stats.slowRequests / stats.requests) * 100 || 0).toFixed(1)}%`}
            />
            <StatCard
              title="Operations"
              value={stats.operations?.toLocaleString() || '0'}
              icon={<Zap className="w-6 h-6 text-purple-500" />}
              color="purple"
              subtitle={`Avg: ${stats.avgOperationDuration}ms`}
            />
          </div>

          {/* Performance Distribution */}
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
              Response Time Distribution
            </h3>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">P50 (Median)</p>
                <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">{stats.p50}ms</p>
              </div>
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">P95</p>
                <p className="text-2xl font-bold text-orange-600 dark:text-orange-400">{stats.p95}ms</p>
              </div>
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">P99</p>
                <p className="text-2xl font-bold text-red-600 dark:text-red-400">{stats.p99}ms</p>
              </div>
            </div>
          </div>

          {/* Top Slow Endpoints Preview */}
          {endpoints.length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Slowest Endpoints (Top 5)
                </h3>
                <button
                  onClick={() => setActiveView('endpoints')}
                  className="text-sm text-blue-500 hover:text-blue-600"
                >
                  View All →
                </button>
              </div>
              <div className="space-y-2">
                {endpoints.slice(0, 5).map((endpoint, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded">
                    <div className="flex-1">
                      <span className="font-mono text-sm text-gray-900 dark:text-white">
                        {endpoint.method} {endpoint.path}
                      </span>
                      <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
                        ({endpoint.count} requests)
                      </span>
                    </div>
                    <div className="text-right">
                      <div className="font-semibold text-orange-600 dark:text-orange-400">
                        {endpoint.avgDuration}ms
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        P95: {endpoint.p95}ms
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Timeline View */}
      {activeView === 'timeline' && (
        <div className="space-y-6">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
              Performance Over Time
            </h3>
            <ResponsiveContainer width="100%" height={400}>
              <AreaChart data={timeline}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis 
                  dataKey="timestamp" 
                  tickFormatter={(ts) => new Date(ts).toLocaleTimeString()}
                  stroke="#9ca3af"
                />
                <YAxis stroke="#9ca3af" label={{ value: 'Duration (ms)', angle: -90, position: 'insideLeft' }} />
                <Tooltip 
                  labelFormatter={(ts) => new Date(ts).toLocaleString()}
                  contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151' }}
                />
                <Legend />
                <Area type="monotone" dataKey="avgDuration" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.3} name="Avg Duration" />
                <Area type="monotone" dataKey="p95" stroke="#f59e0b" fill="#f59e0b" fillOpacity={0.2} name="P95" />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
              Request Volume Over Time
            </h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={timeline}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis 
                  dataKey="timestamp" 
                  tickFormatter={(ts) => new Date(ts).toLocaleTimeString()}
                  stroke="#9ca3af"
                />
                <YAxis stroke="#9ca3af" label={{ value: 'Requests', angle: -90, position: 'insideLeft' }} />
                <Tooltip 
                  labelFormatter={(ts) => new Date(ts).toLocaleString()}
                  contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151' }}
                />
                <Bar dataKey="count" fill="#10b981" name="Request Count" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Endpoints View */}
      {activeView === 'endpoints' && (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="p-6">
            <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
              Endpoint Performance Analysis
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                      Endpoint
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                      Requests
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                      Avg (ms)
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                      P50
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                      P95
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                      P99
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                      Max (ms)
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                      Error Rate
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {endpoints.map((endpoint, idx) => (
                    <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-1 text-xs rounded font-semibold ${
                            endpoint.method === 'GET' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' :
                            endpoint.method === 'POST' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                            endpoint.method === 'PUT' ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400' :
                            'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                          }`}>
                            {endpoint.method}
                          </span>
                          <span className="font-mono text-sm text-gray-900 dark:text-white">
                            {endpoint.path}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center text-gray-900 dark:text-white">
                        {endpoint.count}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`font-semibold ${
                          parseFloat(endpoint.avgDuration) >= 500 ? 'text-red-600 dark:text-red-400' :
                          parseFloat(endpoint.avgDuration) >= 200 ? 'text-orange-600 dark:text-orange-400' :
                          'text-green-600 dark:text-green-400'
                        }`}>
                          {endpoint.avgDuration}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center text-gray-600 dark:text-gray-400">
                        {endpoint.p50}
                      </td>
                      <td className="px-4 py-3 text-center text-gray-600 dark:text-gray-400">
                        {endpoint.p95}
                      </td>
                      <td className="px-4 py-3 text-center text-gray-600 dark:text-gray-400">
                        {endpoint.p99}
                      </td>
                      <td className="px-4 py-3 text-center text-gray-600 dark:text-gray-400">
                        {endpoint.maxDuration}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`font-semibold ${
                          parseFloat(endpoint.errorRate) >= 5 ? 'text-red-600 dark:text-red-400' :
                          parseFloat(endpoint.errorRate) >= 1 ? 'text-orange-600 dark:text-orange-400' :
                          'text-green-600 dark:text-green-400'
                        }`}>
                          {endpoint.errorRate}%
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Tenants View */}
      {activeView === 'tenants' && (
        <div className="space-y-6">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
              Performance by Tenant
            </h3>
            
            {/* Tenant Performance Chart */}
            {tenants.length > 0 && (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={tenants.slice(0, 10)}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis dataKey="tenantCode" stroke="#9ca3af" />
                  <YAxis stroke="#9ca3af" />
                  <Tooltip contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151' }} />
                  <Legend />
                  <Bar dataKey="requestCount" fill="#3b82f6" name="Requests" />
                  <Bar dataKey="avgDuration" fill="#10b981" name="Avg Duration (ms)" />
                </BarChart>
              </ResponsiveContainer>
            )}

            {/* Tenant Table */}
            <div className="mt-6 overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                      Tenant
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                      Requests
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                      Avg (ms)
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                      P95 (ms)
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                      Slow
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                      Errors
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                      Error Rate
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {tenants.map((tenant, idx) => (
                    <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                      <td className="px-4 py-3 font-semibold text-gray-900 dark:text-white">
                        {tenant.tenantCode}
                      </td>
                      <td className="px-4 py-3 text-center text-gray-900 dark:text-white">
                        {tenant.requestCount.toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`font-semibold ${
                          parseFloat(tenant.avgDuration) >= 500 ? 'text-red-600 dark:text-red-400' :
                          parseFloat(tenant.avgDuration) >= 200 ? 'text-orange-600 dark:text-orange-400' :
                          'text-green-600 dark:text-green-400'
                        }`}>
                          {tenant.avgDuration}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center text-gray-600 dark:text-gray-400">
                        {tenant.p95}
                      </td>
                      <td className="px-4 py-3 text-center text-gray-600 dark:text-gray-400">
                        {tenant.slowRequests}
                      </td>
                      <td className="px-4 py-3 text-center text-gray-600 dark:text-gray-400">
                        {tenant.errors}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`font-semibold ${
                          parseFloat(tenant.errorRate) >= 5 ? 'text-red-600 dark:text-red-400' :
                          parseFloat(tenant.errorRate) >= 1 ? 'text-orange-600 dark:text-orange-400' :
                          'text-green-600 dark:text-green-400'
                        }`}>
                          {tenant.errorRate}%
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Slow Requests View */}
      {activeView === 'slow' && (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="p-6">
            <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-orange-500" />
              Slow Requests (&gt; 500ms)
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                      Timestamp
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                      Endpoint
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                      Duration
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                      Tenant
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {slowRequests.map((req, idx) => (
                    <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                        {new Date(req.timestamp).toLocaleString()}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-1 text-xs rounded font-semibold ${
                            req.method === 'GET' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' :
                            req.method === 'POST' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                            req.method === 'PUT' ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400' :
                            'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                          }`}>
                            {req.method}
                          </span>
                          <span className="font-mono text-sm text-gray-900 dark:text-white truncate max-w-md">
                            {req.path}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`font-bold ${
                          req.durationMs >= 1000 ? 'text-red-600 dark:text-red-400' :
                          'text-orange-600 dark:text-orange-400'
                        }`}>
                          {req.durationMs}ms
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`px-2 py-1 text-xs rounded ${
                          req.status >= 200 && req.status < 300 ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                          req.status === 304 ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' :
                          req.status >= 400 && req.status < 500 ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400' :
                          'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                        }`}>
                          {req.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                        {req.tenantCode || '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// StatCard Component
const StatCard = ({ title, value, icon, color, subtitle }) => {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">{title}</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">{value}</p>
          {subtitle && (
            <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">{subtitle}</p>
          )}
        </div>
        <div>{icon}</div>
      </div>
    </div>
  );
};

export default PerformanceMonitoring;
